import React, { memo, useCallback, useState, useMemo } from 'react'
import { Box } from '@material-ui/core'

import { Modal } from 'Components/Modal'
import { ModalBody } from 'Components/Modal/ModalBody'
import { ModalFooter } from 'Components/Modal/ModalFooter'
import { ModalHeader } from 'Components/Modal/ModalHeader'
import { ModalTitle } from 'Components/Modal/ModalTitle'
import Form from 'Components/Form'
import { useFormChildLogic } from 'Components/Form'
import { Input } from 'Components/Form/Input'
import { Textarea } from 'Components/Form/Textarea'
import { SelectDashboard } from 'Components/Form/SelectDashboard'
import { AutoCompleteOldDesign } from 'Components/Form/AutoCompleteOldDesign'
import SubmitButton from 'Components/Form/SubmitButton'
import Button from 'Components/Form/Button'
import {
  required,
  requiredCommaSeparatedValues,
  number,
} from 'Utils/Validators/formValidation'

import useGraphQLFetch from 'Utils/Hooks/useGraphQLFetch'
import { getCampaigns as getCampaignsGQL } from 'Module/graphQL'
import validateFormAddCode from 'Utils/Validators/addCodeForm'
import ConfirmationModalBody from '../ConfirmationModalBody'
import {
  STATUS_CAMPAIGN_ENUM_ACTIVE,
  STATUS_CAMPAIGN_ENUM_DRAFT,
} from '../../../../shared/constants'

const mapResponse = data =>
  data.campaigns.edges
    .map(({ node }) => ({
      cityPlans: node.cityPlans.nodes,
      clientId: node.client.id,
      label: node.name,
      value: node.id,
      artworksLean: node.artworksLean,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

function AddCodeModal({
  showReduxModal,
  addCodeModalCancel,
  csvModalShow,
  hideReduxModal,
  initialCampaignId,
  initialArtworkId,
  onPrefillApplied,
}) {
  const [pickedCampaign, setPickedCampaign] = useState({})
  const prefillAppliedRef = React.useRef(false)
  const fetchArgs = {
    statuses: [STATUS_CAMPAIGN_ENUM_DRAFT, STATUS_CAMPAIGN_ENUM_ACTIVE],
  }
  const { data = [], isLoaded } = useGraphQLFetch({
    fetchFn: getCampaignsGQL,
    mapResponse,
    defaultFetchArgs: fetchArgs,
  })
  React.useEffect(() => {
    if (initialCampaignId && data && !pickedCampaign.value) {
      const found = data.find(
        c => String(c.value) === String(initialCampaignId),
      )
      if (found) setPickedCampaign(found)
    }
  }, [initialCampaignId, data])

  const normalizedCampaignId = useMemo(() => {
    if (!initialCampaignId) return undefined
    if (!data || !data.length) return initialCampaignId
    const sampleType = typeof data[0].value
    return sampleType === 'number'
      ? Number(initialCampaignId)
      : String(initialCampaignId)
  }, [initialCampaignId, data])
  const { cities, clientId, artworks } = useMemo(() => {
    const campaign =
      data && data.find(campaign => campaign.value === pickedCampaign.value)
    if (campaign) {
      return {
        clientId: campaign.clientId,
        cities: campaign.cityPlans.map(item => ({
          value: item.city.id,
          label: item.city.name,
        })),
        artworks: campaign.artworksLean.map(item => ({
          value: item.id,
          label: `${item.id} - ${item.name}`,
        })),
      }
    } else {
      return {}
    }
  }, [pickedCampaign.value, data])

  const normalizedArtworkId = useMemo(() => {
    if (!initialArtworkId) return undefined
    if (!artworks || !artworks.length) return initialArtworkId
    const sampleType = typeof artworks[0].value
    return sampleType === 'number'
      ? Number(initialArtworkId)
      : String(initialArtworkId)
  }, [initialArtworkId, artworks])

  React.useEffect(() => {
    if (onPrefillApplied && initialCampaignId && !prefillAppliedRef.current) {
      const campaignOk = Boolean(pickedCampaign && pickedCampaign.value)
      const artworkOk =
        normalizedArtworkId === undefined ||
        (artworks || []).some(
          a => String(a.value) === String(normalizedArtworkId),
        )

      if (campaignOk && artworkOk) {
        prefillAppliedRef.current = true
        onPrefillApplied()
      }
    }
  }, [
    onPrefillApplied,
    initialCampaignId,
    pickedCampaign.value,
    normalizedArtworkId,
    artworks,
  ])

  const handleFormUpdate = useCallback(item => {
    if (item.name === 'campaign' && item.value !== undefined) {
      setPickedCampaign(item)
    }
  }, [])

  const handleFormSubmit = useCallback(
    form => {
      const { campaign, city, codes, quantity, tag, artwork } = form

      const values = {
        clientId,
        campaignId: campaign.value,
        cityId: city.value,
        codes: codes.value,
        quantity: parseInt(quantity.value, 10),
        tag: tag.value,
        artworkId: artwork.value,
      }

      try {
        validateFormAddCode(values)
      } catch (e) {
        showReduxModal({
          title: 'Error',
          children: e.message,
          confirmLabel: `Ok`,
          hideCancel: true,
        })
        return
      }

      const flyerCodes = values.codes.split(/[,\s]+/g).filter(code => code)

      const flyersData = flyerCodes.map(code => ({
        flyerCode: code,
        clientId: values.clientId,
        campaignId: values.campaignId,
        cityId: values.cityId,
        quantity: values.quantity,
        tag: values.tag,
        artworkId: values.artworkId,
      }))

      showReduxModal({
        title: 'Confirmation needed',
        children: (
          <ConfirmationModalBody
            flyersData={flyersData}
            hideReduxModal={hideReduxModal}
            modalCancel={addCodeModalCancel}
          />
        ),
        confirmLabel: `Confirm`,
        hideButtons: true,
      })
    },
    [clientId],
  )

  return (
    <Modal show={true}>
      <Form onUpdate={handleFormUpdate} onSubmit={handleFormSubmit}>
        <ModalHeader>
          <ModalTitle>Add flyer codes</ModalTitle>
          {!isLoaded && (
            <Box
              mt={0.5}
              style={{
                fontSize: 12,
                opacity: 0.7,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              Loading campaign data...
            </Box>
          )}
        </ModalHeader>
        <ModalBody>
          <Box display="flex" flexDirection="column">
            <Box display="flex" mt={3} mb={1.5}>
              <Box width="50%" mr={3}>
                <CampaignSelect compains={data} value={normalizedCampaignId} />
              </Box>
              <Box width="50%" ml={3}>
                <CitySelect cities={cities} />
                {cities && cities.length === 0 && (
                  <div style={{ color: 'red', marginTop: 4 }}>
                    There is no city plan created
                  </div>
                )}
              </Box>
            </Box>
            <Box display="flex" mt={3} mb={1.5}>
              <Box width="50%" mr={3}>
                <FlyersQuantityInput />
              </Box>
              <Box width="50%" ml={3}>
                <FlyersArtworkSelect
                  artworks={artworks}
                  value={normalizedArtworkId}
                />
              </Box>
            </Box>
            <Box width="100%" mt={1.5} mb={1.5}>
              <FlyersTagInput />
            </Box>
            <Box width="100%" mt={1.5} mb={1.5}>
              <FlyerCodesTextarea />
            </Box>
          </Box>
        </ModalBody>
        <a
          style={{ marginLeft: 240 }}
          href="javascript:;" // eslint-disable-line
          onClick={csvModalShow}
        >
          or upload as file
        </a>
        <ModalFooter>
          <Button value="cancel" onClick={addCodeModalCancel} />
          <SubmitButton value="confirm" />
        </ModalFooter>
      </Form>
    </Modal>
  )
}

const CampaignSelect = memo(({ compains, value }) => {
  const validators = useMemo(() => [required], [])
  const { updateForm } = useFormChildLogic({
    name: 'campaign',
    validator: validators,
  })
  const options = (compains || []).map(o => ({
    label: o.label,
    value: o.value,
  }))

  const handleChange = value => {
    updateForm(value)
  }

  const [searchText, setSearchText] = React.useState('')
  React.useEffect(() => {
    if (!value) return
    const selected = options.find(o => String(o.value) === String(value))
    if (selected) {
      setSearchText(selected.label)
      updateForm(selected.value)
    }
  }, [value, options])

  return (
    <AutoCompleteOldDesign
      id="campaign"
      name="campaign"
      placeholder=""
      label="Campaign"
      disabled={!compains}
      options={options}
      onChange={handleChange}
      searchText={searchText}
      onUpdateInput={setSearchText}
      cypress_id={'selectCampaign'}
    />
  )
})

const CitySelect = memo(({ cities }) => (
  <SelectDashboard
    label="City"
    name="city"
    disabled={!cities}
    validator={[required]}
    options={cities || []}
    cypress_id={'selectCity'}
  />
))

const FlyerCodesTextarea = memo(() => (
  <Textarea
    label="Flyer codes"
    name="codes"
    placeholder="code-1,code-2,code-3"
    validator={[requiredCommaSeparatedValues]}
    cypress_id={'flyerCodeNames'}
  />
))

const FlyersQuantityInput = memo(() => (
  <Input
    label="Flyer quantity"
    name="quantity"
    placeholder="500"
    validator={[required, number]}
    cypress_id={'flyerCodeQuantity'}
  />
))

const FlyersTagInput = memo(() => (
  <Input
    label="AB Test"
    placeholder="Main"
    name="tag"
    cypress_id={'ABTestTag'}
  />
))

const FlyersArtworkSelect = memo(({ artworks, value }) => (
  <SelectDashboard
    label="Artwork"
    name="artwork"
    validator={[required]}
    options={artworks || []}
    value={value}
    cypress_id={'selectArtwork'}
  />
))

export default AddCodeModal
